/**
 * HM-399 cluster 17 Planning Gantt: drawer destra slide-in con dettagli booking.
 *
 * Card guest + meta (camera, mode, time, durata) + costi (totale + extra + paid)
 * + bottoni Modifica / Check-out / Annulla (solo admin).
 *
 * Il bottone "Check-out" routerizza al flow esistente cluster 16.2 (CheckoutModal
 * o pagina /checkout) — l'integrazione precisa la lascio a Michele perche'
 * dipende dal entry point routing che usa il rest dell'app.
 */
import type { PlanningItem } from '../../api/planning.js';
import { useAuth } from '../../stores/auth.js';

const ROLE_LABEL = {
  admin: 'Admin',
  reception: 'Reception',
  owner: 'Owner',
  housekeeping: 'Housekeeping',
} as const;

// QA sweep 2026-07-22: label leggibili per la nota sola-consultazione
// quando nessuna azione e' disponibile (stato terminale o pagina che non
// fornisce l'handler).
const STATUS_NOTE_LABEL: Partial<Record<PlanningItem['status'], string>> = {
  pending: 'in attesa',
  confirmed: 'confermata',
  checked_in: 'in corso',
  checked_out: 'conclusa',
  cancelled: 'cancellata',
  no_show: 'no-show',
  conflict_unresolved: 'in conflitto',
};

function formatItalianDateTime(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function durationHours(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  const h = ms / 3_600_000;
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(1)}h`;
}

export function BookingDetailDrawer({
  booking,
  onClose,
  onCheckOut,
  onModify,
  onCancel,
}: {
  booking: PlanningItem;
  onClose: () => void;
  // QA sweep 2026-07-22: handler opzionali — il drawer e' riusato anche
  // dalla pagina Prenotazioni che non offre tutte le azioni del planning.
  // Il bottone compare solo se il chiamante fornisce l'azione E lo stato
  // la consente (FSM).
  onCheckOut?: () => void;
  onModify?: () => void;
  onCancel?: () => void;
}) {
  const user = useAuth((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  // QA sweep 2026-07-22 (bug MEDIO): azioni allineate alla FSM
  // (apps/api-local/src/domain/booking-fsm.ts LEGAL_TRANSITIONS):
  //  - reschedule/Modifica: solo booking ancora "vivi" (pending/confirmed/
  //    checked_in);
  //  - cancel/Annulla: legale SOLO da pending/confirmed (mai da
  //    checked_in/checked_out);
  //  - check_out: solo da checked_in (gia' cosi').
  // Su stati terminali il drawer e' sola consultazione.
  const canModify =
    !!onModify &&
    (booking.status === 'pending' ||
      booking.status === 'confirmed' ||
      booking.status === 'checked_in');
  const canCancel = !!onCancel && (booking.status === 'pending' || booking.status === 'confirmed');
  const canCheckOut = !!onCheckOut && booking.status === 'checked_in';
  const showCancel = isAdmin && canCancel;
  const readOnly = !canModify && !canCheckOut && !showCancel;

  return (
    <div
      className="planning-drawer-overlay"
      // biome-ignore lint/a11y/useSemanticElements: usare <dialog> nativo richiede ::backdrop + show()/close() API che cluster 17.1 polish potra' adottare; per ora overlay div semplice.
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      data-testid="booking-detail-drawer"
    >
      <div className="planning-drawer">
        <div className="planning-drawer-header">
          <div className="planning-drawer-title">{booking.guestName ?? 'Walk-in'}</div>
          <button
            type="button"
            className="planning-drawer-close"
            onClick={onClose}
            aria-label="Chiudi"
            data-testid="drawer-close"
          >
            ✕
          </button>
        </div>
        <div className="planning-drawer-body">
          {/* QA sweep 2026-07-22: codice prenotazione visibile nel drawer
              (la pagina Prenotazioni ci arriva cercando per codice). */}
          <div className="planning-drawer-row">
            <div className="label">Codice</div>
            <div className="value" style={{ fontFamily: 'monospace', fontSize: 13 }}>
              {booking.publicCode}
            </div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Camera</div>
            <div className="value">{booking.roomCode ?? '—'}</div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Modalità</div>
            <div className="value">{booking.service === 'day_use' ? 'Day Use' : 'Pernotto'}</div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Check-in previsto</div>
            <div className="value">{formatItalianDateTime(booking.startAt)}</div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Check-out previsto</div>
            <div className="value">{formatItalianDateTime(booking.endAt)}</div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Durata</div>
            <div className="value">{durationHours(booking.startAt, booking.endAt)}</div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Stato</div>
            <div className="value">{booking.status}</div>
          </div>
          <div className="planning-drawer-row">
            <div className="label">Totale</div>
            <div className="value">€ {Number(booking.totalAmount).toFixed(2)}</div>
          </div>
          {Number(booking.extraAmount) > 0 && (
            <div className="planning-drawer-row">
              <div className="label">Ore extra</div>
              <div className="value">
                {booking.extraHours}h · € {Number(booking.extraAmount).toFixed(2)}
              </div>
            </div>
          )}
          <div className="planning-drawer-row">
            <div className="label">Pagato</div>
            <div className="value">€ {Number(booking.paidAmount).toFixed(2)}</div>
          </div>
        </div>
        <div className="planning-drawer-footer">
          {canModify && (
            <button
              type="button"
              className="planning-drawer-btn"
              onClick={onModify}
              data-testid="drawer-modify"
            >
              Modifica
            </button>
          )}
          {canCheckOut && (
            <button
              type="button"
              className="planning-drawer-btn primary"
              onClick={onCheckOut}
              data-testid="drawer-checkout"
            >
              Check-out
            </button>
          )}
          {showCancel && (
            <button
              type="button"
              className="planning-drawer-btn danger"
              onClick={onCancel}
              data-testid="drawer-cancel"
              title={
                user?.role ? `Solo admin (tu: ${ROLE_LABEL[user.role] ?? user.role})` : 'Solo admin'
              }
            >
              Annulla
            </button>
          )}
          {readOnly && (
            <span
              data-testid="drawer-readonly"
              style={{ fontSize: 13, color: '#7A6957', padding: '8px 0' }}
            >
              Prenotazione {STATUS_NOTE_LABEL[booking.status] ?? booking.status} — sola
              consultazione
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
